<!--
  PaginationBar Component
  Pagination with page size selector.
  Shows "1-20 von 142" when totalCount is known, otherwise "Seite X".
-->
<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import { _ } from 'svelte-i18n';
  import ChevronLeft from 'lucide-svelte/icons/chevron-left';
  import ChevronRight from 'lucide-svelte/icons/chevron-right';
  import Button from './Button.svelte';

  export let page = 0;
  export let pageSize = 20;
  export let totalCount: number | null = null;
  export let hasMore = false;
  export let hasPrev = false;

  const dispatch = createEventDispatcher<{
    pageChange: number;
    pageSizeChange: number;
  }>();

  const pageSizeOptions = [10, 20, 50];

  $: rangeStart = page * pageSize + 1;
  $: rangeEnd =
    totalCount != null ? Math.min((page + 1) * pageSize, totalCount) : (page + 1) * pageSize;

  function handlePrev() {
    if (hasPrev) {
      dispatch('pageChange', page - 1);
    }
  }

  function handleNext() {
    if (hasMore) {
      dispatch('pageChange', page + 1);
    }
  }

  function handlePageSizeChange(e: Event) {
    const target = e.target as HTMLSelectElement;
    dispatch('pageSizeChange', parseInt(target.value, 10));
  }
</script>

<div class="flex flex-wrap justify-between items-center px-4 py-3 border-t border-border gap-2">
  <!-- Page size selector -->
  <div class="flex items-center gap-2">
    <select
      value={pageSize}
      on:change={handlePageSizeChange}
      class="px-2 py-1 bg-bg-alt border border-border rounded text-sm text-text focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent/30 cursor-pointer"
    >
      {#each pageSizeOptions as size (size)}
        <option value={size}>{size}</option>
      {/each}
    </select>
    <span class="text-xs text-text-muted">{$_('table.pagination.perPage')}</span>
  </div>

  <!-- Range display + nav buttons -->
  <div class="flex items-center gap-3">
    {#if totalCount != null}
      <span class="text-sm text-text-muted">
        {rangeStart}–{rangeEnd}
        {$_('table.pagination.of')}
        {totalCount}
      </span>
    {:else}
      <span class="text-sm text-text-muted">
        {$_('table.pagination.page')}
        {page + 1}
      </span>
    {/if}

    <div class="flex gap-1">
      <Button variant="secondary" size="sm" disabled={!hasPrev} on:click={handlePrev}>
        <ChevronLeft class="w-4 h-4" strokeWidth={1.5} />
      </Button>
      <Button variant="secondary" size="sm" disabled={!hasMore} on:click={handleNext}>
        <ChevronRight class="w-4 h-4" strokeWidth={1.5} />
      </Button>
    </div>
  </div>
</div>
